import DoubleFileIcon from '@cp/src/assets/icons/DoubleFileIcon';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import Button from '@primitives/Button/Button';
import cx from 'classnames';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DeleteIconOutlined, DoneIcon, DownloadIcon, RefreshIcon } from '@navi/web-ui/lib/icons';
import ErrorIcon from '@cp/src/assets/icons/ErrorIcon';
import {
  BULK_UPLOAD_FLOW,
  CREATE_NEW,
  NDRA,
  ROLE_OPTIONS,
  UPLOAD_NUMBERS,
  validationOfFile
} from '../interface';
import { downloadSampleFile } from '../allAgentsAction';
import { isCSVFileName } from '../UploadHistory/types.ts/types';

const FileUploader = ({
  onFileUpload,
  uploadedFile,
  setUploadedFile,
  selectedTab
}: {
  onFileUpload: (file: File) => void;
  uploadedFile: File | null;
  setUploadedFile: React.Dispatch<React.SetStateAction<File | null>>;
  selectedTab: string;
}) => {
  const dispatch = useDispatch();
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleDownloadClick = () => {
    if (selectedTab === CREATE_NEW) {
      dispatch(downloadSampleFile(BULK_UPLOAD_FLOW.CREATE));
    } else if (selectedTab === UPLOAD_NUMBERS) {
      dispatch(downloadSampleFile(BULK_UPLOAD_FLOW.NUMBER));
    } else if (selectedTab === NDRA) {
      dispatch(downloadSampleFile(BULK_UPLOAD_FLOW.NDRA));
    } else {
      dispatch(downloadSampleFile(BULK_UPLOAD_FLOW.EDIT));
    }
  };

  const handleFile = (file: File | undefined) => {
    if (file) {
      setUploadedFile(file);
      if (!validationOfFile(file)) {
        setHasError(true);
        setTimeout(() => {
          if (hiddenFileInput && hiddenFileInput?.current) {
            hiddenFileInput.current.value = '';
          }
          setHasError(false);
        }, 2000);
      } else {
        setHasError(false);
      }
    }
  };
  const handleUploadClick = () => {
    hiddenFileInput.current?.focus();
    hiddenFileInput.current?.click();
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setUploadedFile(selectedFile);
      onFileUpload(selectedFile);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const selectedFile = event.dataTransfer.files?.[0];
    if (isCSVFileName(selectedFile.name)) {
      handleFile(selectedFile);
      onFileUpload(selectedFile);
    }
  };

  const handleDelete = () => {
    setUploadedFile(null);
    if (hiddenFileInput && hiddenFileInput?.current) {
      hiddenFileInput.current.value = '';
    }
    setHasError(false);
  };

  return (
    <>
      {!uploadedFile ? (
        <div>
          <div
            className={cx({
              'flex flex-col justify-center items-center border  border-gray-border py-[12px] border-dashed bg-[#F5F9FF] rounded cursor-pointer':
                true,
              'bg-blue-bg border-blue-cyan': isDragging && !uploadedFile
            })}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onClick={handleUploadClick}
          >
            <DoubleFileIcon />
            <input
              id="fileUpload"
              type="file"
              accept=".csv"
              onChange={handleChange}
              ref={hiddenFileInput}
              style={{ display: 'none' }}
            />
            <div className={'pt-[8px] text-[#0276FE] font-medium text-[13px]'}>
              <Button variant="text">Click to upload or drag and drop here</Button>{' '}
            </div>
            <div className={'pt-[4px]'}>
              <Typography variant={'p5'} className={'text-grayscale-3 text-xs font-normal'}>
                CSV (One file at a time)
              </Typography>
            </div>
          </div>
          <Button
            onClick={handleDownloadClick}
            variant="text"
            className={
              'text-blue-base bg-bg-secondary py-[10px] mt-[16px] px-[16px] rounded w-[100%] flex justify-start'
            }
            startAdornment={<DownloadIcon color="var(--blue-base)" />}
          >
            Download sample
          </Button>
        </div>
      ) : validationOfFile(uploadedFile) ? (
        <div className="flex flex-row justify-between items-center border  border-gray-border border-dashed p-[16px] rounded">
          <div className="flex flex-row gap-[8px] items-center truncate">
            <div className="w-[20px]">
              <DoneIcon color="var(--blue-base)" width={20} height={20} />
            </div>
            <Typography variant={'p4'} className={'text-grayscale-2 text-sm font-normal truncate'}>
              {uploadedFile.name}
            </Typography>
          </div>
          <div className="cursor-pointer">
            <DeleteIconOutlined width={20} height={20} onClick={handleDelete} />
          </div>
        </div>
      ) : (
        <div className="flex flex-row justify-between items-center border  border-[#CACED5] border-dashed p-[16px] rounded">
          <div className="flex flex-row gap-[8px] items-center truncate">
            <div className="w-[20px]">
              <ErrorIcon fillColor="var(--red-base)" width={20} height={20} />
            </div>
            <Typography variant={'p4'} className={'text-red-base text-sm font-normal truncate'}>
              {uploadedFile.name}
            </Typography>
          </div>
          <div className="cursor-pointer flex gap-[12px]">
            <DeleteIconOutlined width={20} height={20} onClick={handleDelete} />
          </div>
        </div>
      )}
    </>
  );
};

export default FileUploader;
